<script setup lang="ts">
import { ref, computed, watch, h, onMounted, type VNodeChild } from 'vue';
import type { DataTableColumns, DataTableSortState } from 'naive-ui';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaClippedText from '@/components/design/base/GaiaClippedText.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import {
  ExtractionPromptsService,
  type ExtractionPromptFilter,
} from '@/services/contractManagement/extractionPromptsService';
import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import type { ContractManagementViewColumnBulkAddResult, ExtractionPromptSearchEntry } from '@/api';
import { logger } from '@/utils/logger';
import { responseTypeLabel } from '@/utils/contractManagementView/columnQuestion';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Pick one of the organisation's extraction prompts: on the first tab, first the contract type
 * it belongs to, then the prompt itself; on the second, straight from the standalone prompts.
 * Loads when mounted, so its host mounts it each time it is shown (a modal's or drawer's body).
 */
interface Props {
  /** Persisted mode: marks the prompts this view already asks (from the backend). */
  viewUid?: string;
  /** Preview mode: the prompts the wizard's columns already link. */
  usedPromptUids?: string[];
  /**
   * Persisted mode, when picking a new column: each contract type row can add all of its
   * prompts to the view as columns at once.
   */
  bulkAdd?: boolean;
}

interface Emits {
  (e: 'select-prompt', prompt: ExtractionPromptSearchEntry): void;
  /** A contract type's prompts were added to the view as columns. */
  (e: 'bulk-added', result: ContractManagementViewColumnBulkAddResult): void;
}

const props = withDefaults(defineProps<Props>(), {
  viewUid: undefined,
  usedPromptUids: () => [],
  bulkAdd: false,
});

const emit = defineEmits<Emits>();
const message = useGaiaToast();
const contractTypeStore = useContractTypeStore();

/** A row of the first level: a contract type (or, behind its own tab, the standalone prompts). */
interface PromptGroupRow {
  key: string;
  name: string;
  description: string;
  group: ExtractionPromptFilter;
  /** Null while not loaded. */
  promptCount: number | null;
}

// Search state: filters the groups locally, the prompts on the server
const searchQuery = ref('');
const promptSearch = ref('');
const searchInputRef = ref<InstanceType<typeof GaiaSearchInput>>();

/** Empties the field both levels share, dropping a search of the other level still debouncing. */
function clearSearch(): void {
  searchInputRef.value?.cancel();
  searchQuery.value = '';
  promptSearch.value = '';
}

// Pagination state - Groups
const groupPage = ref(1);
const groupPageSize = ref(10);

// Pagination state - Prompts (remote)
const promptPage = ref(1);
const promptPageSize = ref(10);

const isLoadingPrompts = ref(false);
/** Numbers the prompt loads, so only the latest one's response is shown. */
let promptRequest = 0;

const selectedGroup = ref<PromptGroupRow | null>(null);
const prompts = ref<ExtractionPromptSearchEntry[]>([]);
const promptCount = ref(0);
const standalonePromptCount = ref<number | null>(null);

const groupRows = computed((): PromptGroupRow[] =>
  contractTypeStore.sortedContractTypes.map(type => ({
    key: type.uid,
    name: type.name,
    description: type.description,
    group: { contractTypeUids: [type.uid] },
    promptCount: type.extraction_prompt_count ?? null,
  }))
);

// ==================== Tabs ====================

type PickerTab = 'types' | 'standalone';
const activeTab = ref<PickerTab>('types');

/** The standalone prompts' tab lists them as a group of its own, without the contract type step. */
const standaloneGroup: PromptGroupRow = {
  key: 'standalone',
  name: 'Standalone prompts',
  description: '',
  group: { withoutContractType: true },
  promptCount: null,
};

const tabs = computed((): GaiaTabBarItem[] => [
  { key: 'types', label: 'Contract types', count: contractTypeStore.contractTypes.length },
  {
    key: 'standalone',
    label: 'Standalone prompts',
    count: standalonePromptCount.value ?? undefined,
  },
]);

function selectTab(key: string): void {
  if (key === activeTab.value) return;
  activeTab.value = key as PickerTab;
  if (key === 'standalone') handleGroupRowClick(standaloneGroup);
  else handleBackToGroups();
}

// Computed: filtered groups (client-side search on loaded data)
const filteredGroups = computed(() => {
  if (!searchQuery.value.trim()) {
    return groupRows.value;
  }
  const query = searchQuery.value.toLowerCase();
  return groupRows.value.filter(row => row.name.toLowerCase().includes(query));
});

// Sorted here, not by naive: GaiaTable pages the rows before naive sees them, so a
// naive sorter would only reorder the current page.
type GroupSortKey = 'name' | 'promptCount';
const groupSort = ref<{ key: GroupSortKey; order: 'ascend' | 'descend' } | null>(null);

function compareGroups(a: PromptGroupRow, b: PromptGroupRow, key: GroupSortKey): number {
  return key === 'name'
    ? a.name.localeCompare(b.name)
    : (a.promptCount ?? -1) - (b.promptCount ?? -1) || a.name.localeCompare(b.name);
}

const sortedGroups = computed((): PromptGroupRow[] => {
  const sort = groupSort.value;
  if (!sort) return filteredGroups.value;
  const direction = sort.order === 'ascend' ? 1 : -1;
  return [...filteredGroups.value].sort((a, b) => direction * compareGroups(a, b, sort.key));
});

function groupSortOrder(key: GroupSortKey): 'ascend' | 'descend' | false {
  return groupSort.value?.key === key ? groupSort.value.order : false;
}

function handleGroupSortChange(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (Array.isArray(sorter)) return;
  groupSort.value = sorter?.order
    ? { key: sorter.columnKey as GroupSortKey, order: sorter.order }
    : null;
  groupPage.value = 1;
}

// The chip after the breadcrumb — how much sits at the level you're looking at.
const levelCount = computed((): string =>
  selectedGroup.value
    ? `${promptCount.value} prompts`
    : `${contractTypeStore.contractTypes.length} contract types`
);

// Paging is naive's own (the table is fed the whole filtered list and slices it
// locally), so the page controls sit in the table's footer strip rather than as
// a separate row underneath it.
const groupPagination = computed(() => ({
  page: groupPage.value,
  pageSize: groupPageSize.value,
  pageSizes: [10, 20, 50],
  showSizePicker: true,
  itemCount: filteredGroups.value.length,
  onUpdatePage: (page: number): void => {
    groupPage.value = page;
  },
  onUpdatePageSize: (size: number): void => {
    groupPageSize.value = size;
    groupPage.value = 1;
  },
}));

const promptPagination = computed(() => ({
  page: promptPage.value,
  pageSize: promptPageSize.value,
  pageSizes: [10, 20, 50],
  showSizePicker: true,
  itemCount: promptCount.value,
  onUpdatePage: (page: number): void => {
    promptPage.value = page;
    void loadPrompts();
  },
  onUpdatePageSize: (size: number): void => {
    promptPageSize.value = size;
    promptPage.value = 1;
    void loadPrompts();
  },
}));

const usedPromptUidSet = computed(() => new Set(props.usedPromptUids));

function isInView(prompt: ExtractionPromptSearchEntry): boolean {
  return Boolean(prompt.view_column_uids?.length) || usedPromptUidSet.value.has(prompt.uid);
}

// Group table columns - clickable rows
const groupColumns = computed<DataTableColumns<PromptGroupRow>>(() => [
  {
    title: 'Contract type',
    key: 'name',
    width: 240,
    sorter: true,
    sortOrder: groupSortOrder('name'),
    render(row): VNodeChild {
      return h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => row.name);
    },
  },
  {
    title: 'Description',
    key: 'description',
    ellipsis: true,
    render(row): VNodeChild {
      return h(GaiaText, { size: 'dense', tone: 'tertiary' }, () => row.description);
    },
  },
  {
    title: 'Prompts',
    key: 'promptCount',
    width: 136,
    align: 'right',
    sorter: true,
    sortOrder: groupSortOrder('promptCount'),
    render(row): VNodeChild {
      return h(GaiaText, { size: 'dense', tone: 'tertiary' }, () => row.promptCount ?? '–');
    },
  },
  ...(canBulkAdd.value
    ? [
        {
          title: '',
          key: 'bulkAdd',
          width: 96,
          align: 'right' as const,
          render: renderBulkAdd,
        },
      ]
    : []),
]);

// ==================== Adding a contract type's prompts at once ====================

const canBulkAdd = computed(() => props.bulkAdd && Boolean(props.viewUid));
/** The contract type whose prompts the confirmation offers to add; null while it is closed. */
const pendingBulkAdd = ref<{ row: PromptGroupRow; contractTypeUid: string } | null>(null);
const bulkAdding = ref(false);

// The endpoint takes a contract type, so the standalone prompts have no "Add all".
function renderBulkAdd(row: PromptGroupRow): VNodeChild {
  const contractTypeUid = row.group.contractTypeUids?.[0];
  if (!contractTypeUid || !row.promptCount) return null;
  return h(
    GaiaButton,
    {
      size: 'tiny',
      // The row's own click opens the contract type.
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        pendingBulkAdd.value = { row, contractTypeUid };
      },
    },
    () => 'Add all'
  );
}

function columnCount(count: number | null): string {
  return `${count ?? 0} ${count === 1 ? 'column' : 'columns'}`;
}

async function runBulkAdd(): Promise<void> {
  if (!props.viewUid || !pendingBulkAdd.value) return;
  const { row, contractTypeUid } = pendingBulkAdd.value;
  bulkAdding.value = true;
  try {
    const result = await ContractViewsService.addContractTypeColumns(
      props.viewUid,
      contractTypeUid
    );
    const added = result.created.length;
    const skipped = result.existing.length;
    if (added === 0) {
      message.info(`The view already asks all of ${row.name}'s prompts`);
    } else {
      message.success(
        `Added ${added} ${added === 1 ? 'column' : 'columns'}${
          skipped > 0 ? `, ${skipped} already in the view` : ''
        }`
      );
    }
    pendingBulkAdd.value = null;
    emit('bulk-added', result);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add the prompts');
  } finally {
    bulkAdding.value = false;
  }
}

// Prompt table columns - rows are clickable for selection
const promptColumns = computed<DataTableColumns<ExtractionPromptSearchEntry>>(() => [
  {
    title: 'Name',
    key: 'display_name',
    width: 220,
    render(row): VNodeChild {
      return h(
        'div',
        { style: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' } },
        [
          h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => row.display_name),
          isInView(row)
            ? h(GaiaTag, { variant: 'muted', size: 'tiny' }, () => 'In this view')
            : null,
        ]
      );
    },
  },
  {
    title: 'Prompt',
    key: 'prompt',
    render(row): VNodeChild {
      return h(GaiaClippedText, { text: row.prompt, max: 80 });
    },
  },
  {
    title: 'Type',
    key: 'response_type',
    width: 110,
    render(row): VNodeChild {
      return h(GaiaTag, { variant: 'muted' }, () => responseTypeLabel(row.response_type));
    },
  },
]);

// Load one page of the selected group's prompts
async function loadPrompts(): Promise<void> {
  const group = selectedGroup.value;
  if (!group) return;
  const request = ++promptRequest;
  // A slower response for an earlier group, page or search must not overwrite the latest one,
  // nor end its loading state.
  const isStale = (): boolean => request !== promptRequest || selectedGroup.value !== group;
  isLoadingPrompts.value = true;
  try {
    const result = await ExtractionPromptsService.search({
      filter: group.group,
      text: promptSearch.value.trim(),
      offset: (promptPage.value - 1) * promptPageSize.value,
      limit: promptPageSize.value,
      viewUid: props.viewUid,
    });
    if (isStale()) return;
    prompts.value = result.results;
    promptCount.value = result.result_count;
  } catch (error) {
    if (isStale()) return;
    logger.error('Error loading prompts:', error);
    message.error('Failed to load prompts');
  } finally {
    if (request === promptRequest) isLoadingPrompts.value = false;
  }
}

// The standalone prompts have no contract type to carry their count; a one-result search gives it.
async function loadStandalonePromptCount(): Promise<void> {
  try {
    const result = await ExtractionPromptsService.search({
      filter: { withoutContractType: true },
      text: '',
      offset: 0,
      limit: 1,
    });
    standalonePromptCount.value = result.result_count;
  } catch (error) {
    logger.error('Error counting standalone prompts:', error);
  }
}

// Handle clicking on a group row
function handleGroupRowClick(row: PromptGroupRow): void {
  selectedGroup.value = row;
  prompts.value = [];
  promptCount.value = 0;
  clearSearch();
  promptPage.value = 1;
  void loadPrompts();
}

// Handle going back to the groups list
function handleBackToGroups(): void {
  selectedGroup.value = null;
  prompts.value = [];
  promptCount.value = 0;
  clearSearch();
  groupPage.value = 1;
}

function handlePromptSearch(value: string): void {
  if (!selectedGroup.value) return;
  promptSearch.value = value;
  promptPage.value = 1;
  void loadPrompts();
}

function groupRowProps(row: PromptGroupRow): Record<string, unknown> {
  return {
    style: 'cursor: pointer;',
    onClick: (): void => {
      handleGroupRowClick(row);
    },
  };
}

function promptRowProps(row: ExtractionPromptSearchEntry): Record<string, unknown> {
  return {
    style: 'cursor: pointer;',
    onClick: (): void => {
      emit('select-prompt', row);
    },
  };
}

onMounted(() => {
  // Refetched, not ensureLoaded: the counts change whenever a column creates a prompt.
  void contractTypeStore.fetchContractTypes();
  void loadStandalonePromptCount();
});

// Reset the group pagination when its search changes
watch(searchQuery, () => {
  if (!selectedGroup.value) groupPage.value = 1;
});
</script>

<template>
  <n-flex vertical :size="14" :wrap="false">
    <gaia-tab-bar :items="tabs" :active="activeTab" @select="selectTab" />

    <!-- Where you are, and how to narrow it down. -->
    <n-flex align="center" :size="8" :wrap="false">
      <template v-if="activeTab === 'types' && selectedGroup">
        <gaia-button text size="small" @click="handleBackToGroups">
          <template #icon>
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M9.5 3.5 5 8l4.5 4.5" />
            </svg>
          </template>
          Contract types
        </gaia-button>
        <gaia-text size="dense" tone="faint">/</gaia-text>
      </template>

      <!-- The standalone tab already names its list and counts it. -->
      <template v-if="activeTab === 'types'">
        <gaia-text size="body" weight="semibold" tone="ink" truncate>
          {{ selectedGroup ? selectedGroup.name : 'All contract types' }}
        </gaia-text>
        <gaia-tag variant="muted">{{ levelCount }}</gaia-tag>
      </template>

      <gaia-search-input
        ref="searchInputRef"
        v-model:value="searchQuery"
        :placeholder="selectedGroup ? 'Search prompts' : 'Search contract types'"
        :style="{ marginLeft: 'auto' }"
        @search="handlePromptSearch"
      />
    </n-flex>

    <!-- Level 1: the contract types -->
    <template v-if="!selectedGroup">
      <gaia-table
        v-if="contractTypeStore.loading || filteredGroups.length > 0"
        :columns="groupColumns"
        :data="sortedGroups"
        :loading="contractTypeStore.loading"
        :pagination="groupPagination"
        :max-height="380"
        :row-key="(row: PromptGroupRow) => row.key"
        :row-props="groupRowProps"
        @update:sorter="handleGroupSortChange"
      />
      <gaia-empty-state
        v-else
        dashed
        :title="
          searchQuery.trim() ? 'No contract type matches your search' : 'No contract types yet'
        "
      />
    </template>

    <!-- Level 2: the prompts of the one you picked -->
    <template v-else>
      <gaia-table
        v-if="isLoadingPrompts || prompts.length > 0"
        :columns="promptColumns"
        :data="prompts"
        :loading="isLoadingPrompts"
        remote
        :pagination="promptPagination"
        :max-height="380"
        :row-key="(row: ExtractionPromptSearchEntry) => row.uid"
        :row-props="promptRowProps"
      />
      <gaia-empty-state
        v-else
        dashed
        :title="promptSearch.trim() ? 'No prompt matches your search' : 'No prompts here yet'"
      />
    </template>

    <gaia-modal
      :show="pendingBulkAdd !== null"
      width="460px"
      title="Add columns from contract type"
      :mask-closable="!bulkAdding"
      :close-on-esc="!bulkAdding"
      @update:show="(value: boolean) => (value || bulkAdding ? undefined : (pendingBulkAdd = null))"
    >
      <gaia-text v-if="pendingBulkAdd" size="body" tone="subtle">
        This adds {{ columnCount(pendingBulkAdd.row.promptCount) }} from
        <strong>{{ pendingBulkAdd.row.name }}</strong> to the view.
      </gaia-text>
      <template #action>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button :disabled="bulkAdding" @click="pendingBulkAdd = null">Cancel</gaia-button>
          <gaia-button type="primary" :loading="bulkAdding" @click="runBulkAdd">
            Add {{ columnCount(pendingBulkAdd?.row.promptCount ?? null) }}
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </n-flex>
</template>
